import { toTitleCase } from "utils/formatText";
import type { PricingOrg, PricingPlan } from "../../zodios/apiDefinitions/pricing";
import TrellisNavLink from "components/core/basic/NavLink";

export const RULES_BADGE_TEXT: Record<string, string> = {
  CLEARANCE: "Clearance",
  MARGIN_PROFILING: "Margin Profiling",
  PROFILE: "Margin Profiling",
  BSR: "BSR",
  CUSTOM: "Custom",
};

export const SEGMENT_TEXT: Record<string, string> = {
  sales_pressure: "Sales Pressure",
  profit_potential: "Profit Potential",
  right_price: "Right Price",
  out_of_stock: "Out of Stock",
  slow_movers: "Slow Movers",
  no_price: "Slow Movers",
  new_plan: "Profiling",
};

export const DAYS_ON_HAND_LABELS = {
  Deficient: "fuchsia",
  Healthy: "green",
  Excess: "yellow",
  Overstock: "mauve",
};

export const PRICING_STATUS_LABELS = {
  enabled: "Enrolled",
  disabled: "Paused",
  not_enrolled: "Not Enrolled",
};

export const BUCKET_COLORS: Record<string, string> = {
  "No Forecast": "rgba(46,224,202,0.55)",
  "No Forecast Price": "rgba(145,232,225,0.55)",
  Steady: "rgba(44,175,254,0.55)",
  Overforecast: "rgba(254,106,53,0.55)",
  Underforecast: "rgba(0,226,114,0.55)",
  Opportunity: "rgba(254,181,106,0.55)",
  "No Sales": "rgba(107,138,188,0.55)",
  Monitor: "rgba(213,104,251,0.55)",
  Recovery: "rgba(250,75,66,0.55)",
  Scaling: "rgba(84,79,197,0.55)",
  Rescue: "rgba(227, 121, 179, 0.55)",
  Good: "hsla(226, 43.10%, 38.60%, 0.55)",
};

const types = [
  {
    value: "same_strategy",
    label: "Dynamic Price",
    description: "Same strategy for all products",
  },
  {
    value: "same_price",
    label: "Dynamic Price",
    description: "Same price for all products",
  },
  {
    value: "competitor",
    label: "Competitor",
    description: "Competitor Matching",
  },
  {
    value: "rules_engine",
    label: "Rules Engine",
    description: "Custom rules for the pricing plan",
  },
] as const;

export const getPlanType = (pricingPlan: {
  plan_type: string;
  is_auto: boolean;
  current_program?: { template_type: string };
}) => {
  if (pricingPlan.plan_type === "rules_engine") {
    if (pricingPlan.is_auto) {
      let templateType = null;
      if (pricingPlan.current_program?.template_type) {
        templateType = pricingPlan.current_program.template_type;
      }
      if (templateType != null && templateType !== "MARGIN_PROFILING" && templateType !== "CR_UNITS") {
        return templateType;
      }

      return types[1].label;
    }
  }

  let planType: (typeof types)[number] = types[1];

  for (let i = 0; i < types.length; i++) {
    const pt = types[i]!;
    if (pt.value === pricingPlan.plan_type) {
      planType = pt;
      break;
    }
  }

  return planType.label;
};

export const PricingRulesNav = ({ pricingPlan, hasModule }: { pricingPlan: PricingPlan; hasModule?: boolean }) => {
  if (!hasModule && pricingPlan.plan_type === "rules_engine") {
    if (pricingPlan.is_auto) {
      return null;
    }
  }

  let name = "Pricing Rules";
  if (hasModule && pricingPlan.is_auto) {
    name = "Pricing Rules (Debug)";
  }

  return <TrellisNavLink eventKey="rules_engine">{name}</TrellisNavLink>;
};

const calcInventoryTarget = (
  rules: Exclude<PricingOrg["supply_target_program"], null>["rules"],
  inventoryOnHand: number,
  desiredInventoryLevel: number,
) => {
  for (let i = 0; i < rules.length; i++) {
    const minLevel = (rules[i]!.min / 100) * desiredInventoryLevel;
    const maxLevel = (rules[i]!.max / 100) * desiredInventoryLevel;

    if (inventoryOnHand >= minLevel && inventoryOnHand <= maxLevel) {
      return rules[i]!["status"];
    }
  }

  return "N/A";
};

export const getInventoryStatus = (pricingPlan: PricingPlan | null) => {
  if (!pricingPlan?.override_supply) {
    return null;
  }

  if (!pricingPlan.pricing_org.supply_target_program?.rules) {
    if (pricingPlan.days_of_supply < (pricingPlan.target_supply ?? 0)) {
      return "Deficient";
    }

    return "Healthy";
  }

  const inventoryTarget = calcInventoryTarget(
    pricingPlan.pricing_org.supply_target_program.rules,
    pricingPlan.days_of_supply,
    pricingPlan.target_supply ?? 0,
  );
  return toTitleCase(inventoryTarget);
};

/**
 * Consider `offsetType` as "relative vs absolute" more than "currency vs percent".
 * Most offsets are applied to currency fields, hence the names, but if it's applied to, say ACOS, that's when "currency" should be read as "absolute".
 */
export const applyOffset = (value: number, offsetAmount: number, offsetType: "currency" | "percent") => {
  if (offsetType === "currency") {
    return value + offsetAmount;
  }

  return value + value * (offsetAmount / 100);
};

const getForecastBucketName = (bucket: string) => {
  return bucket;
};

export const ForecastBadge = ({ bucket }: { bucket: string }): JSX.Element => {
  const bucketName = getForecastBucketName(bucket);
  return (
    <span
      className="badge rounded-pill me-3"
      style={{
        backgroundColor: BUCKET_COLORS[bucketName]?.replace("0.55", "0.75") || "#ccc",
        color: "#fff",
      }}
    >
      {bucketName}
    </span>
  );
};
